import { noop } from 'lodash';
import { memo, type ReactNode } from 'react';

import { useMemoizedBundle } from '@didaskill/ui/utils';

import { ListRenderer } from '../renderers/list/ListRenderer';
import { DropdownListContext } from './DropdownListContext';

export type DropdownListProps = {
  /** Whether to show item separator. */
  withItemSeparator?: boolean;
  /** Value of currently selected list item. */
  selectedItem?: string;
  /** Called with the new item value when clicking an item. */
  onClickItem?: (item: string | null) => void;
  /** Keys of checked items. */
  checkedItems?: string[];
  /** Checked keys onChange handler. */
  onChangeCheckedItems?: (
    newCheckedItems: string[] | ((currentCheckedItems: string[]) => string[]),
  ) => void;
  /** List items and sublists. */
  children?: ReactNode;
  /** Class name. */
  className?: string;
};

const DEFAULT_CHECKED_KEYS = [];

export const DropdownListBase = ({
  withItemSeparator = false,
  selectedItem = null,
  onClickItem = noop,
  checkedItems = DEFAULT_CHECKED_KEYS,
  onChangeCheckedItems = noop,
  children = null,
  className = undefined,
}: DropdownListProps) => {
  const contextValue = useMemoizedBundle({
    selectedItem,
    onClickItem,
    checkedItems,
    onChangeCheckedItems,
  });

  return (
    <DropdownListContext.Provider value={contextValue}>
      <ListRenderer className={className} withItemSeparator={withItemSeparator}>
        {children}
      </ListRenderer>
    </DropdownListContext.Provider>
  );
};

DropdownListBase.displayName = 'DropdownList';

export const DropdownList = memo(DropdownListBase);
